import type { ShareIntentInput, ShareIntents } from "./types";

/**
 * Build social-share intent URLs from a public link + a localized message.
 *
 * Pure — no `window`/`navigator`, so it runs identically in SSR, unit tests,
 * and both portals. Each channel uses its documented web-intent endpoint:
 *  - WhatsApp: `wa.me/?text=` — message + link in one body (WhatsApp auto-links the URL).
 *  - X / Twitter: `intent/tweet?text=&url=` — separate text and url params.
 *  - Facebook: `sharer.php?u=` — URL only (Facebook ignores prefilled text by policy).
 *
 * Instagram is intentionally absent: the web has no "share to IG post" intent.
 * Callers reach Instagram via copy-link + the native share sheet
 * (`navigator.share`) on mobile.
 */
export function buildShareIntents({
  url,
  text,
}: ShareIntentInput): ShareIntents {
  const encodedUrl = encodeURIComponent(url);
  const whatsappBody = encodeURIComponent(text ? `${text} ${url}` : url);
  return {
    whatsapp: `https://wa.me/?text=${whatsappBody}`,
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text,
    )}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
  };
}
